import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle, Info, X } from "lucide-react";
import { createContext, useCallback, useContext, useState } from "react";
import { cn } from "../lib/utils";

type ToastType = "success" | "error" | "info";

interface ToastItem {
	id: string;
	message: string;
	type: ToastType;
}

interface ToastContextValue {
	toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<ToastItem[]>([]);

	const toast = useCallback((message: string, type: ToastType = "info") => {
		const id = crypto.randomUUID();
		setToasts((prev) => [...prev, { id, message, type }]);
		setTimeout(() => {
			setToasts((prev) => prev.filter((t) => t.id !== id));
		}, 4000);
	}, []);

	const remove = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const icons = {
		success: <CheckCircle className="w-4 h-4 text-green-400" />,
		error: <AlertCircle className="w-4 h-4 text-red-400" />,
		info: <Info className="w-4 h-4 text-primary" />,
	};

	return (
		<ToastContext.Provider value={{ toast }}>
			{children}
			<div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 pointer-events-none">
				<AnimatePresence>
					{toasts.map((t) => (
						<motion.div
							key={t.id}
							initial={{ opacity: 0, y: 20, scale: 0.95 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: -10, scale: 0.95 }}
							className={cn(
								"pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-lg border text-sm font-medium min-w-[200px]",
								"bg-card border-border text-foreground"
							)}
						>
							{icons[t.type]}
							<span className="flex-1">{t.message}</span>
							<button
								type="button"
								onClick={() => remove(t.id)}
								className="p-1 rounded hover:bg-secondary transition-colors"
							>
								<X className="w-3 h-3 text-muted-foreground" />
							</button>
						</motion.div>
					))}
				</AnimatePresence>
			</div>
		</ToastContext.Provider>
	);
}
